<article class="iching-reader" [attr.data-iching-hexagram]="hexagram().number">
  <header class="iching-reader__head">
    <hd-iching-hexagram-figure class="iching-reader__figure" [figure]="hexagram().figure" size="large" [highlightLine]="focusLine()" />
    <div class="iching-reader__titles">
      <div class="iching-reader__number-row">
        <span class="iching-reader__number">{{ hexagram().number }}</span>
        <span class="iching-reader__source-badge">{{ hexagram().source.translator }}</span>
        @if (hdGateInfoAvailable()) {
          <button
            type="button"
            class="iching-reader__hd-gate"
            data-test-id="iching-reader-hd-gate"
            [title]="t('humanDesign.iching.reader.hdGateInfo', { gate: hexagram().number })"
            [attr.aria-label]="t('humanDesign.iching.reader.hdGateInfo', { gate: hexagram().number })"
            (click)="hdGateInfoRequested.emit(hexagram().number)"
          >
            <i class="fas fa-circle-info" aria-hidden="true"></i>
          </button>
        }
      </div>
      @if (hideTitle()) {
        <button type="button" class="iching-reader__reveal" data-test-id="iching-reader-reveal-title" (click)="titleRevealRequested.emit()">
          {{ t('humanDesign.iching.reading.revealName') }}
        </button>
      } @else {
        <div class="iching-reader__name">{{ hexagram().chineseName }}</div>
        <h2 class="iching-reader__title">{{ hexagram().title }}</h2>
      }
      <div class="iching-reader__trigrams">
        <span>{{ t('humanDesign.iching.reader.above') }} {{ trigramLabels().upper.full }}</span>
        <span>{{ t('humanDesign.iching.reader.below') }} {{ trigramLabels().lower.full }}</span>
      </div>
      <div class="iching-reader__nuclear">
        {{ t('humanDesign.iching.reader.nuclear') }} {{ trigramLabels().nuclearUpper.short }} / {{ trigramLabels().nuclearLower.short }}
      </div>
      @if (hexagram().rulers.governing.length || hexagram().rulers.constituting.length) {
        <div class="iching-reader__rulers">
          {{ t('humanDesign.iching.reader.rulers', { governing: hexagram().rulers.governing.join(', ') || '-', constituting: hexagram().rulers.constituting.join(', ') || '-' }) }}
        </div>
      }
    </div>
  </header>

  <p class="iching-reader__legend">{{ t('humanDesign.iching.reader.legend') }}</p>

  @if (!hideExplanations() && hexagram().text.introduction.length) {
    <section class="iching-reader__intro">
      <span class="iching-note-label">{{ t('humanDesign.iching.reader.introduction') }}</span>
      <hd-iching-prose class="iching-commentary" [paragraphs]="hexagram().text.introduction" path="text.introduction" [footnotes]="hexagram().footnotes" />
    </section>
  }

  <section class="iching-reader__judgement iching-block">
    <h3 class="iching-block__heading">{{ t('humanDesign.iching.reader.judgement') }}</h3>
    <span class="iching-verse__label">{{ t('humanDesign.iching.reader.oracleText') }}</span>
    <div class="iching-verse" [class.iching-verse--markable]="markable()">
      @if (markable()) {
        @for (verse of judgementWords(); track $index) {
          <div>
            @for (token of verse; track $index) {
              <span
                class="iching-word"
                [class.is-marked]="isMarked(token.word)"
                [class.iching-term]="token.term !== undefined"
                [attr.title]="token.term?.short"
                (click)="clickWord(token.word)"
                >{{ token.word }}</span
              >{{ $last ? '' : ' ' }}
            }
          </div>
        }
      } @else {
        @for (verse of judgementSegments(); track $index) {
          <div><ng-container *ngTemplateOutlet="segmentsTpl; context: { $implicit: verse }" /></div>
        }
      }
    </div>
    <ng-container
      *ngTemplateOutlet="explain; context: { id: 'judgement', paragraphs: hexagram().text.judgement.commentary, path: 'text.judgement.commentary' }"
    />
  </section>

  <section class="iching-reader__image iching-block">
    <h3 class="iching-block__heading">{{ t('humanDesign.iching.reader.image') }}</h3>
    <span class="iching-verse__label">{{ t('humanDesign.iching.reader.oracleText') }}</span>
    <div class="iching-verse">
      @for (verse of imageSegments(); track $index) {
        <div><ng-container *ngTemplateOutlet="segmentsTpl; context: { $implicit: verse }" /></div>
      }
    </div>
    <ng-container *ngTemplateOutlet="explain; context: { id: 'image', paragraphs: hexagram().text.image.commentary, path: 'text.image.commentary' }" />
  </section>

  <section class="iching-reader__lines iching-block" [class.iching-reader__lines--carousel]="linesAsCarousel()">
    <h3 class="iching-block__heading">{{ t('humanDesign.iching.reader.lines') }}</h3>
    @if (linesAsCarousel()) {
      <!-- Phone: één lijn per slide, altijd open; de startslide volgt de focus- of bewegende lijn. -->
      <e-carousel class="iching-reader__carousel" data-test-id="iching-lines-carousel" [(index)]="carouselIndex" [labels]="carouselLabels()">
        @for (line of hexagram().text.lines; track line.position) {
          <div
            eCarouselSlide
            class="iching-line iching-line--slide"
            [attr.data-iching-line]="hexagram().number + '.' + line.position"
            [class.is-focus]="line.position === focusLine()"
          >
            <div class="iching-line__label iching-line__label--static">
              <span class="iching-line__position">{{ line.position }}</span>
              <span class="iching-line__label-text">{{ line.label }}</span>
              @if (line.ruler) {
                <span class="iching-line__ruler">{{ line.ruler === 'governing' ? '○' : '□' }}</span>
              }
            </div>
            <span class="iching-verse__label">{{ t('humanDesign.iching.reader.oracleText') }}</span>
            <div class="iching-verse">
              @for (verse of lineSegments()[$index]; track $index) {
                <div><ng-container *ngTemplateOutlet="segmentsTpl; context: { $implicit: verse }" /></div>
              }
            </div>
            <ng-container
              *ngTemplateOutlet="explain; context: { id: 'line' + line.position, paragraphs: line.commentary, path: 'text.lines[' + (line.position - 1) + '].commentary' }"
            />
          </div>
        }
      </e-carousel>
    }
    @for (line of linesAsCarousel() ? [] : hexagram().text.lines; track line.position) {
      <div class="iching-line" [attr.data-iching-line]="hexagram().number + '.' + line.position" [class.is-focus]="line.position === focusLine()"
        [class.is-dim]="focusLine() !== undefined && line.position !== focusLine()"
      >
        <button type="button" class="iching-line__label" (click)="toggleLine(line.position)" [attr.aria-expanded]="lineOpen()(line.position)">
          <span class="iching-line__position">{{ line.position }}</span>
          <span class="iching-line__label-text">{{ line.label }}</span>
          @if (line.ruler) {
            <span class="iching-line__ruler">{{ line.ruler === 'governing' ? '○' : '□' }}</span>
          }
        </button>
        @if (lineOpen()(line.position)) {
          <span class="iching-verse__label">{{ t('humanDesign.iching.reader.oracleText') }}</span>
          <div class="iching-verse">
            @for (verse of lineSegments()[$index]; track $index) {
              <div><ng-container *ngTemplateOutlet="segmentsTpl; context: { $implicit: verse }" /></div>
            }
          </div>
          <ng-container
            *ngTemplateOutlet="explain; context: { id: 'line' + line.position, paragraphs: line.commentary, path: 'text.lines[' + (line.position - 1) + '].commentary' }"
          />
        }
      </div>
    }
    @if (hexagram().text.allLines; as all) {
      <div class="iching-line iching-line--all">
        <div class="iching-line__label iching-line__label--static">{{ all.label }}</div>
        <span class="iching-verse__label">{{ t('humanDesign.iching.reader.oracleText') }}</span>
        <div class="iching-verse">
          @for (verse of all.text; track $index) {
            <div>{{ verse }}</div>
          }
        </div>
        <ng-container *ngTemplateOutlet="explain; context: { id: 'allLines', paragraphs: all.commentary, path: 'text.allLines.commentary' }" />
      </div>
    }
  </section>

  @if (!hideRelations()) {
    <hd-iching-relations
      class="iching-reader__relations"
      [number]="hexagram().number"
      [entries]="relationEntries()"
      (hexagramSelected)="relatedSelected.emit($event)"
    />
  }

  <section class="iching-reader__commentary">
    @if (commentary(); as c) {
      <hd-iching-commentary [commentary]="c" [footnotes]="hexagram().footnotes" />
    } @else if (commentaryBusy()) {
      <div class="iching-reader__busy">{{ t('humanDesign.iching.reader.loading') }}</div>
    } @else {
      <button type="button" class="iching-reader__load-commentary" data-test-id="iching-load-commentary" (click)="commentaryRequested.emit()">
        {{ t('humanDesign.iching.reader.loadCommentary') }}
      </button>
    }
  </section>

  <footer class="iching-reader__source">{{ hexagram().source.work }}, {{ hexagram().source.translator }} ({{ hexagram().source.year }})</footer>
</article>

<ng-template #explain let-id="id" let-paragraphs="paragraphs" let-path="path">
  @if (!hideExplanations() && paragraphs.length) {
    <button type="button" class="iching-explain-toggle" (click)="toggleExplain(id)" [attr.aria-expanded]="explainOpen().has(id)">
      {{ explainOpen().has(id) ? t('humanDesign.iching.reader.hideExplanation') : t('humanDesign.iching.reader.showExplanation') }}
    </button>
    @if (explainOpen().has(id)) {
      <span class="iching-note-label">{{ t('humanDesign.iching.reader.translatorNote') }}</span>
      <hd-iching-prose class="iching-commentary" [paragraphs]="paragraphs" [path]="path" [footnotes]="hexagram().footnotes" />
    }
  }
</ng-template>

<!-- Versregel met begrippen: stippellijn en de korte uitleg als tooltip (title, zoals eBtn [tooltip]). -->
<ng-template #segmentsTpl let-segments>
  @for (segment of segments; track $index) {
    @if (segment.term; as term) {
      <span class="iching-term" [attr.title]="term.short">{{ segment.text }}</span>
    } @else {
      <ng-container>{{ segment.text }}</ng-container>
    }
  }
</ng-template>
